import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ConfirmDialogService } from '../../dialog/confirm-dialog/confirm-dialog.service';
import {
  GeneralLedgerReference,
  GeneralledgerService,
} from './generalledger.service';

/** Dependencies required to run the shared general ledger delete flow. */
export interface DeleteGeneralLedgerOptions {
  /** Id of the general ledger to delete. */
  id: string;
  service: GeneralledgerService;
  dialogService: ConfirmDialogService;
  destroyRef: DestroyRef;
  /** Called after the general ledger has been removed successfully. */
  onDeleted: () => void;
}

/**
 * Runs the shared "Verwijderen" flow for a general ledger:
 * checks for existing references and either shows a blocking dialog
 * or asks for confirmation before deleting.
 */
export function deleteGeneralLedgerWithChecks(options: DeleteGeneralLedgerOptions): void {
  const { id, service, dialogService, destroyRef } = options;

  service
    .getReferenceCountForDeleteCheck(id)
    .pipe(take(1), takeUntilDestroyed(destroyRef))
    .subscribe({
      next: (references) => {
        if (references.length > 0) {
          showDeleteNotPossibleDialog(dialogService, references);
        } else {
          confirmAndDeleteGeneralLedger(options);
        }
      },
      error: () => undefined,
    });
}

function showDeleteNotPossibleDialog(
  dialogService: ConfirmDialogService,
  references: GeneralLedgerReference[],
): void {
  const lines = references.map(
    (reference) => `- ${reference.tablename} heeft ${reference.count} verwijzingen`,
  );
  const message = [
    'Kan grootboek niet verwijderen de volgende verwijzingen bestaan nog:',
    ...lines,
  ].join('\n');

  dialogService.open({
    width: '300px',
    title: 'Verwijderen niet mogelijk',
    message,
    confirmText: 'Ok',
    variant: 'error',
  });
}

function confirmAndDeleteGeneralLedger(options: DeleteGeneralLedgerOptions): void {
  const { id, service, dialogService, destroyRef, onDeleted } = options;

  dialogService.open({
    width: '320px',
    title: 'Verwijderen?',
    message: 'Grootboek verwijderen?',
    cancelText: 'Nee',
    confirmText: 'Ja',
    variant: 'error',
  });

  dialogService
    .confirmed()
    .pipe(
      take(1),
      switchMap((confirmed) => {
        if (!confirmed) {
          return EMPTY;
        }

        return service.removeGeneralLedger(id);
      }),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe({
      next: () => onDeleted(),
      error: () => undefined,
    });
}
